import {
    List,
    Datagrid,
    TextField,
    NumberField,
    ReferenceField,
    EditButton,
    Edit,
    Create,
    SimpleForm,
    TextInput,
    NumberInput,
    ReferenceInput,
    ReferenceArrayInput,
    SelectArrayInput,
    SelectInput,
    required,
    useRecordContext,
    ChipField,
    Pagination,
    BooleanInput,
    DateField,
    SearchInput,
    TopToolbar,
    FilterButton,
    CreateButton,
    ExportButton,
    TabbedForm,
    FormTab,
    Button,
    useRefresh,
    useNotify,
    useDataProvider,
} from 'react-admin';
import { Chip } from '@mui/material';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ImageManager from '../components/ImageManager';
import HtmlInput from '../components/HtmlInput';



// Transform function to convert categories array to category_ids
const transformIncoming = (record) => {
    if (!record) return null;

    // Extract only the fields we want to edit
    const {
        id,
        ID,
        name,
        display_name,
        description,
        price,
        final_price,
        discount_type,
        discount_value,
        stock_quantity,
        sku,
        brand_id,
        categories,
        manage_inventory,
        low_stock_alert,
        weight,
        width,
        height,
        length,
    } = record;

    return {
        id: id || ID, // Handle both id and ID
        name,
        display_name,
        description,
        price,
        final_price,
        discount_type: discount_type || 'none',
        discount_value: discount_value || 0,
        stock_quantity,
        sku,
        brand_id,
        category_ids: categories?.map(cat => cat.ID) || [],
        manage_inventory: manage_inventory || false,
        low_stock_alert: low_stock_alert || 5,
        weight: weight || 0,
        width: width || 0,
        height: height || 0,
        length: length || 0,
    };
};

// Transform function for saving data
const transformOutgoing = (data) => {
    if (!data) return null;

    // Remove any fields that shouldn't be sent to the API
    const {
        id,
        categories,
        created_at,
        updated_at,
        deleted_at,
        brand,
        images,
        final_price, // Remove final_price as it's calculated by the database
        ...rest
    } = data;

    // Ensure numeric fields are numbers
    return {
        ...rest,
        price: Number(rest.price),
        discount_value: Number(rest.discount_value || 0),
        stock_quantity: Number(rest.stock_quantity),
        weight: Number(rest.weight || 0),
        width: Number(rest.width || 0),
        height: Number(rest.height || 0),
        length: Number(rest.length || 0),
        low_stock_alert: Number(rest.low_stock_alert || 5),
        manage_inventory: Boolean(rest.manage_inventory),
    };
};

const CategoryField = () => {
    const record = useRecordContext();
    if (!record || !record.categories) return null;
    return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
            {record.categories.map(category => (
                <ChipField key={category.ID} record={category} source="name" />
            ))}
        </div>
    );
};

// Enhanced BooleanField for status display
const ActiveStatusField = () => {
    const record = useRecordContext();
    if (!record) return null;
    
    return (
        <Chip 
            label={record.is_active ? 'Active' : 'Inactive'}
            color={record.is_active ? 'success' : 'error'}
            size="small"
            variant="filled"
        />
    );
};

// Toggle Active Button Component
const ToggleActiveButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const refresh = useRefresh();
    const notify = useNotify();

    if (!record) return null;

    const handleToggle = async () => {
        try {
            await dataProvider.toggleProductStatus(record.id);
            refresh();
            notify(
                `Product ${record.is_active ? 'deactivated' : 'activated'} successfully`,
                { type: 'success' }
            );
        } catch (error) {
            notify('Error updating product status', { type: 'error' });
            console.error('Toggle error:', error);
        }
    };

    return (
        <Button
            onClick={handleToggle}
            label={record.is_active ? 'Deactivate' : 'Activate'}
            startIcon={record.is_active ? <ToggleOffIcon /> : <ToggleOnIcon />}
            sx={{
                color: record.is_active ? 'error.main' : 'success.main',
                '&:hover': {
                    backgroundColor: record.is_active ? 'error.light' : 'success.light',
                    color: 'white',
                }
            }}
        />
    );
};

const DiscountField = () => {
    const record = useRecordContext();
    if (!record) return <span>-</span>;
    
    if (record.discount_type === 'none' || !record.discount_value) {
        return <span>-</span>;
    }
    
    if (record.discount_type === 'flat') {
        return <span>{record.discount_value.toLocaleString()}</span>;
    }
    
    if (record.discount_type === 'percent') {
        return <span>{record.discount_value}% off</span>;
    }
    
    return <span>-</span>;
};

const productFilters = [
    <SearchInput source="q" alwaysOn placeholder="Search by name, display name, or description..." resettable />,
    <ReferenceInput source="category" reference="categories" label="Category">
        <SelectInput optionText="name" emptyText="All Categories" />
    </ReferenceInput>,
    <ReferenceInput source="brand" reference="brands" label="Brand">
        <SelectInput optionText="name" emptyText="All Brands" />
    </ReferenceInput>,
    <SelectInput
        source="is_active"
        label="Status"
        choices={[
            { id: '', name: 'All Products' },
            { id: 'true', name: 'Active Products' },
            { id: 'false', name: 'Inactive Products' },
        ]}
        emptyText="All Products"
        emptyValue=""
    />,
];

const ListActions = () => (
    <TopToolbar>
        <FilterButton />
        <CreateButton />
        <ExportButton />
    </TopToolbar>
);

export const ProductList = () => (
    <List 
        pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
        perPage={10}
        sort={{ field: 'id', order: 'DESC' }}
        filters={productFilters}
        filterDefaultValues={{ q: '' }}
        actions={<ListActions />}
    >
        <Datagrid
            rowStyle={(record) => ({
                backgroundColor: record.is_active ? 'inherit' : '#f5f5f5',
                opacity: record.is_active ? 1 : 0.7,
            })}
        >
            <TextField source="id" />
            <TextField source="name" />
            <TextField source="display_name" />
            <NumberField source="price" label="Original Price" />
            <NumberField source="final_price" label="Final Price" />
            <DiscountField label="Discount" />
            <NumberField source="stock_quantity" />
            <NumberField source="view_count" label="Views" sortable />
            <TextField source="sku" />
            <CategoryField label="Categories" />
            <ReferenceField source="brand_id" reference="brands">
                <TextField source="name" />
            </ReferenceField>
            <NumberField source="ordering" />
            <ActiveStatusField />
            <DateField source="CreatedAt" showTime={true}/>
            <DateField source="UpdatedAt" showTime={true}/>
            <ToggleActiveButton />
            <EditButton />
        </Datagrid>
    </List>
);

const ProductTitle = () => {
    const record = useRecordContext();
    return <span>Product {record ? `"${record.name}"` : ''}</span>;
};

export const ProductEdit = () => (
    <Edit 
        title={<ProductTitle />} 
        transform={transformOutgoing} 
        mutationMode="pessimistic"
    >
        <TabbedForm 
            transform={transformIncoming}
            defaultValues={transformIncoming}
        >
            <FormTab label="Product Details">
                <TextInput source="name" validate={[required()]} />
                <TextInput source="display_name" validate={[required()]} />
                <TextInput source="sku" />
                <HtmlInput source="description" label="Description (HTML)" validate={[required()]} helperText="Use the toolbar to format text. HTML will be sanitized for security." />
                <NumberInput source="price" validate={[required()]} min={0} label="Original Price" />
                <SelectInput 
                    source="discount_type" 
                    choices={[
                        { id: 'none', name: 'No Discount' },
                        { id: 'flat', name: 'Flat Amount' },
                        { id: 'percent', name: 'Percentage' },
                    ]} 
                    defaultValue="none"
                    label="Discount Type"
                />
                <NumberInput 
                    source="discount_value" 
                    min={0} 
                    defaultValue={0}
                    label="Discount Value"
                    helperText="For flat: amount in currency. For percent: percentage value (e.g., 10 for 10%)"
                />
                <NumberField source="final_price" label="Final Price (Auto-calculated)" disabled />
                <NumberInput source="stock_quantity" defaultValue={0} validate={[required()]} min={0}/>
                <ReferenceArrayInput 
                    source="category_ids" 
                    reference="categories"
                    perPage={1000} // Fetch up to 1000 categories to ensure all are available
                >
                    <SelectArrayInput
                        optionText="name"
                        fullWidth
                        helperText="Select one or more categories"
                        parse={v => v ? v.map(Number) : []}
                    />
                </ReferenceArrayInput>
                <ReferenceInput 
                    source="brand_id" 
                    reference="brands"
                    perPage={1000}
                >
                    <SelectInput 
                        optionText="name" 
                        validate={[required()]} 
                        fullWidth
                        helperText="Select a brand"
                    />
                </ReferenceInput>
                <BooleanInput source="manage_inventory" defaultValue={true} />
                <NumberInput source="weight" defaultValue={0} min={0}/>
                <NumberInput source="width" defaultValue={0} min={0}/>
                <NumberInput source="height" defaultValue={0} min={0}/>
                <NumberInput source="length" defaultValue={0} min={0}/>
                <NumberInput source="ordering" defaultValue={0}/>
                <BooleanInput source="is_active" defaultValue={true} />
            </FormTab>
            <FormTab label="Images">
                <ImageManagerTab />
            </FormTab>
        </TabbedForm>
    </Edit>
);

// Component to render ImageManager in the tab
const ImageManagerTab = () => {
    const record = useRecordContext();
    return <ImageManager productId={record?.id || record?.ID} />;
};

export const ProductCreate = () => (
    <Create transform={transformOutgoing}>
        <SimpleForm>
            <TextInput source="name" validate={[required()]} />
            <TextInput source="display_name" validate={[required()]} />
            <TextInput source="sku" />
            <HtmlInput source="description" label="Description (HTML)" validate={[required()]} helperText="Use the toolbar to format text. HTML will be sanitized for security." />
            <NumberInput source="price" validate={[required()]} min={0} label="Original Price" />
            <SelectInput 
                source="discount_type" 
                choices={[
                    { id: 'none', name: 'No Discount' },
                    { id: 'flat', name: 'Flat Amount' },
                    { id: 'percent', name: 'Percentage' },
                ]} 
                defaultValue="none"
                label="Discount Type"
            />
            <NumberInput 
                source="discount_value" 
                min={0} 
                defaultValue={0}
                label="Discount Value"
                helperText="For flat: amount in currency. For percent: percentage value (e.g., 10 for 10%)"
            />
            <NumberInput source="stock_quantity" defaultValue={0} validate={[required()]} min={0}/>
            <ReferenceArrayInput 
                source="category_ids" 
                reference="categories"
                perPage={1000}
            >
                <SelectArrayInput
                    optionText="name"
                    fullWidth
                    helperText="Select one or more categories"
                    parse={v => v ? v.map(Number) : []}
                />
            </ReferenceArrayInput>
            <ReferenceInput 
                source="brand_id" 
                reference="brands" 
                perPage={1000}
            >
                <SelectInput 
                    optionText="name" 
                    validate={[required()]} 
                    fullWidth
                    helperText="Select a brand"
                />
            </ReferenceInput>
            <BooleanInput source="manage_inventory" defaultValue={true} />
            <NumberInput source="low_stock_alert" defaultValue={5} min={0}/>
            <NumberInput source="weight" defaultValue={0} min={0}/>
            <NumberInput source="width" defaultValue={0} min={0}/>
            <NumberInput source="height" defaultValue={0} min={0}/>
            <NumberInput source="length" defaultValue={0} min={0}/>
            <NumberInput source="ordering" defaultValue={0}/>
        </SimpleForm>
    </Create>
); 